import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { QuoteService } from "@/gen/omnios/v1/quote_connect";
import { QuoteSourceType } from "@/gen/omnios/v1/quote_pb";
import { MatchMode } from "@/gen/omnios/v1/work_item_pb";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
	ChevronRight,
	Columns3,
	FileUp,
	LayoutList,
	Loader2,
	Plus,
	Search,
	Sparkles,
	X,
	Zap,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/quotes/")({
	component: QuotesPage,
});

const client = createClient(QuoteService, transport);
const partyClient = createClient(PartyService, transport);
const mediaClient = createClient(MediaService, transport);

const ACCEPTED_FILE_TYPES =
	".pdf,.xlsx,.xls,.docx,.doc,.csv,.txt,.jpg,.jpeg,.png";


const STATUS_OPTIONS = [
	"draft",
	"sent",
	"negotiating",
	"accepted",
	"rejected",
	"expired",
	"converted_to_order",
] as const;

const QUOTE_STATUS_COLUMNS = [
	{ id: "draft", color: "hsl(var(--muted-foreground))" },
	{ id: "sent", color: "#3b82f6" },
	{ id: "negotiating", color: "#f59e0b" },
	{ id: "accepted", color: "#22c55e" },
	{ id: "rejected", color: "#ef4444" },
	{ id: "expired", color: "#6b7280" },
	{ id: "converted_to_order", color: "#8b5cf6" },
] as const;

function QuotesPage() {
	const { t } = useTranslation("quotes");
	useBreadcrumbs([{ label: t("title") }]);
	const qc = useQueryClient();
	const navigate = useNavigate();
	const { onGridReady, onStateChanged } = useGridLayout("commerce-quotes");
	const { selectedRows, onSelectionChanged, ActionBar } = useGridSelection();

	const searchParams = useSearch({ from: "/commerce/quotes/" });
	const viewParam =
		(searchParams as Record<string, unknown>).view === "board"
			? "board"
			: "table";

	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [statusFilter, setStatusFilter] = useState("");

	const { gridRef, gridProps, totalRows: total, refreshGrid } = useServerGrid({
		fetchRows: async ({ pageSize, pageToken, sort, search: s }) => {
			const resp = await client.listQuotes({
				search: s,
				status: statusFilter,
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.quotes ?? [],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		search,
		extraFilter: statusFilter || undefined,
		colToField: {
			customerPartyId: "customer_party_id",
			createdAt: "created_at",
		},
	});

	// Board view data
	const { data: boardData } = useQuery({
		queryKey: ["commerce", "quotes-board", search, statusFilter],
		queryFn: () =>
			client.listQuotes({
				search,
				status: statusFilter,
				page: { pageSize: 200, pageToken: "" },
			}),
		enabled: viewParam === "board",
	});
	const boardItems = boardData?.quotes ?? [];

	const kanbanColumns = useMemo(
		() =>
			QUOTE_STATUS_COLUMNS.map((col) => ({
				id: col.id,
				label: t(`status_${col.id}`),
				color: col.color,
			})),
		[t],
	);

	// Create dialog state — driven by ?create search param
	const createOpen = (searchParams as Record<string, unknown>).create === true;

	const [formTitle, setFormTitle] = useState("");
	const [formCustomer, setFormCustomer] = useState("");
	const [formCurrency, setFormCurrency] = useState("RUB");
	const [formValidity, setFormValidity] = useState(30);
	const [formText, setFormText] = useState("");
	const [formFile, setFormFile] = useState<File | null>(null);
	const [dragOver, setDragOver] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const [formMatchMode, setFormMatchMode] = useState<MatchMode>(MatchMode.ACCURATE);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const resetCreateForm = useCallback(() => {
		setFormTitle("");
		setFormCustomer("");
		setFormCurrency("RUB");
		setFormValidity(30);
		setFormText("");
		setFormFile(null);
		setDragOver(false);
		setShowDetails(false);
		setFormMatchMode(MatchMode.ACCURATE);
	}, []);

	const openCreateDialog = useCallback(() => {
		navigate({ to: "/commerce/quotes", search: { create: true }, replace: true });
	}, [navigate]);

	const closeCreateDialog = useCallback(() => {
		resetCreateForm();
		navigate({ to: "/commerce/quotes", search: {}, replace: true });
	}, [navigate, resetCreateForm]);

	const { data: partiesData } = useQuery({
		queryKey: ["parties-for-quote"],
		queryFn: () =>
			partyClient.listParties({ page: { pageSize: 100, pageToken: "" } }),
		enabled: createOpen,
	});
	const parties = partiesData?.parties ?? [];

	const createMut = useMutation({
		mutationFn: async () => {
			const hasSource = formFile || formText.trim();
			const quote = await client.createQuote({
				title:
					formTitle ||
					(formFile ? formFile.name.replace(/\.[^.]+$/, "") : "") ||
					(formText.trim() ? formText.trim().slice(0, 60) : "Новое КП"),
				customerPartyId: formCustomer,
				currency: formCurrency,
				validityDays: formValidity,
				notes: formText.trim(),
				sourceType: QuoteSourceType.MANUAL,
			});

			// Capture values before state reset
			const textToProcess = formText;
			const fileToProcess = formFile;
			const modeToProcess = formMatchMode;

			// Fire-and-forget: process in background, don't block navigation
			if (hasSource) {
				(async () => {
					try {
						let mediaId: string | undefined;
						if (fileToProcess) {
							const buf = await fileToProcess.arrayBuffer();
							const media = await mediaClient.uploadMedia({
								filename: fileToProcess.name,
								mimeType: fileToProcess.type || "application/octet-stream",
								data: new Uint8Array(buf),
								entityType: "quote",
								entityId: quote.id,
							});
							mediaId = media.id;
						}
						const stream = client.processQuote({
							quoteId: quote.id,
							text: textToProcess || "",
							mediaId: mediaId ?? "",
							mode: modeToProcess,
						});
						for await (const _update of stream) {
							// consume
						}
						qc.invalidateQueries({
							queryKey: ["quote-reviews", quote.id],
						});
					} catch (err) {
						console.error("quote processing failed:", err);
						toast.error(
							t("process_error", {
								defaultValue: "Ошибка обработки документа",
							}),
						);
					}
				})();
			}

			return quote;
		},
		onSuccess: (quote) => {
			refreshGrid();
			closeCreateDialog();
			if (formFile || formText.trim()) {
				toast.success(
					t("process", { defaultValue: "Обработка запущена" }),
				);
			}
			navigate({ to: "/commerce/quotes/$id", params: { id: quote.id } });
		},
	});

	// Column defs
	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "title",
				headerName: t("form_title"),
				flex: 2,
				minWidth: 200,
				pinned: "left",
				valueFormatter: (p) =>
					p.data?.title || `\u041A\u041F-${p.data?.id?.slice(0, 8) ?? ""}`,
			},
			{
				field: "customerPartyId",
				headerName: t("customer"),
				width: 120,
				cellClass: "font-mono",
				valueFormatter: (p) => (p.value ? p.value.slice(0, 8) : "\u2014"),
			},
			{
				field: "status",
				headerName: t("status"),
				width: 140,
				filter: false,
				cellRenderer: (p: { data?: any; value: string }) => {
					if (!p.data || !p.value) return null;
					return (
						<span className="inline-flex items-center gap-1.5">
							<CommerceStatusBadge entity="quote" status={p.value} />
							{p.data.isProcessing && (
								<Loader2 className="h-3 w-3 animate-spin text-blue-500" />
							)}
						</span>
					);
				},
			},
			{
				colId: "lineCount",
				headerName: t("lines"),
				width: 80,
				type: "rightAligned",
				cellClass: "font-tabular",
				filter: false,
				valueGetter: (p) => p.data?.lines?.length ?? 0,
				valueFormatter: (p) => (p.value ? String(p.value) : "\u2014"),
			},
			{
				field: "sourceType",
				headerName: t("source"),
				width: 120,
				valueFormatter: (p) => {
					const v = p.data?.sourceType;
					if (v === QuoteSourceType.CASE) return t("source_case");
					if (v === QuoteSourceType.STOREFRONT) return t("source_storefront");
					if (v === QuoteSourceType.API) return t("source_api");
					return t("source_manual");
				},
			},
			{
				field: "createdAt",
				headerName: t("created_at"),
				flex: 1,
				minWidth: 120,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => {
					const ts = p.data?.createdAt;
					return ts
						? new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU")
						: "\u2014";
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: any }) => {
			if (e.data) {
				navigate({
					to: "/commerce/quotes/$id",
					params: { id: e.data.id },
				});
			}
		},
		[navigate],
	);

	// Status bar
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${total} ${t("title", { defaultValue: "\u043A\u043E\u043C\u043C\u0435\u0440\u0447\u0435\u0441\u043A\u0438\u0445 \u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u0439" })}`,
				align: "left" as const,
			},
		],
		[total, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			{/* Search + filter bar */}
			<div className="flex items-center gap-2 px-4 py-2">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						ref={searchRef}
						placeholder={t("search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Select
					value={statusFilter || "all"}
					onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
				>
					<SelectTrigger className="w-[180px] h-9">
						<SelectValue placeholder={t("status")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">
							{t("all_statuses", {
								defaultValue:
									"\u0412\u0441\u0435 \u0441\u0442\u0430\u0442\u0443\u0441\u044B",
							})}
						</SelectItem>
						{STATUS_OPTIONS.map((s) => (
							<SelectItem key={s} value={s}>
								{t(`status_${s}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{/* View switcher */}
				<div className="flex items-center rounded-md border border-border">
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/commerce/quotes",
								search: { view: "table" },
							})
						}
						className={cn(
							"inline-flex items-center justify-center h-7 w-7 rounded-l-md transition-colors",
							viewParam === "table"
								? "bg-accent text-accent-foreground"
								: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
						)}
						title={t("view_table")}
					>
						<LayoutList className="h-3.5 w-3.5" />
					</button>
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/commerce/quotes",
								search: { view: "board" },
							})
						}
						className={cn(
							"inline-flex items-center justify-center h-7 w-7 rounded-r-md transition-colors",
							viewParam === "board"
								? "bg-accent text-accent-foreground"
								: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
						)}
						title={t("view_board")}
					>
						<Columns3 className="h-3.5 w-3.5" />
					</button>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "quotes.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" onClick={openCreateDialog}>
					<Plus className="h-4 w-4 mr-1" />
					{t("create")}
				</Button>
			</div>

			{/* Grid / Board */}
			{viewParam === "board" ? (
				<KanbanBoard<{ id: string; title: string; customerPartyId: string; status: string; lines: any[]; createdAt?: { seconds: bigint } }>
					items={boardItems}
					columns={kanbanColumns}
					getColumnId={(item) => item.status}
					onItemClick={(id) =>
						navigate({
							to: "/commerce/quotes/$id",
							params: { id },
						})
					}
					emptyLabel={t("kanban_empty")}
					unsetLabel={t("kanban_unset_status")}
					renderCard={(quote) => (
						<>
							<div className="flex items-center gap-1.5">
								<span className="font-medium truncate leading-tight">
									{quote.title || `КП-${quote.id.slice(0, 8)}`}
								</span>
							</div>
							<div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
								{quote.customerPartyId && (
									<span className="truncate max-w-[140px]">
										{quote.customerPartyId.slice(0, 8)}
									</span>
								)}
								{quote.lines?.length > 0 && (
									<span className="font-tabular">
										{quote.lines.length} поз.
									</span>
								)}
								{quote.createdAt && (
									<span>
										{new Date(
											Number(quote.createdAt.seconds) * 1000,
										).toLocaleDateString("ru-RU")}
									</span>
								)}
							</div>
						</>
					)}
				/>
			) : (
			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty", { defaultValue: "\u041D\u0435\u0442 \u043A\u043E\u043C\u043C\u0435\u0440\u0447\u0435\u0441\u043A\u0438\u0445 \u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u0439" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_desc", { defaultValue: "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043F\u0435\u0440\u0432\u043E\u0435 \u041A\u041F" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>
			)}

			{/* Create quote dialog */}
			<Dialog
				open={createOpen}
				onOpenChange={(open) => {
					if (open) openCreateDialog();
					else closeCreateDialog();
				}}
			>
				<DialogContent
				className="sm:max-w-lg"
				onDragOver={(e) => {
					e.preventDefault();
					setDragOver(true);
				}}
				onDragLeave={(e) => {
					if (e.currentTarget.contains(e.relatedTarget as Node)) return;
					setDragOver(false);
				}}
				onDrop={(e) => {
					e.preventDefault();
					setDragOver(false);
					const file = e.dataTransfer.files?.[0];
					if (file) setFormFile(file);
				}}
			>
					<DialogHeader>
						<DialogTitle>{t("create_dialog_title")}</DialogTitle>
						<p className="text-sm text-muted-foreground">
							{t("create_dialog_hint", {
								defaultValue:
									"Вставьте текст заявки, перетащите файл или заполните вручную",
							})}
						</p>
					</DialogHeader>

					<div className="flex flex-col gap-4 py-2">
						{/* Client selector — always visible */}
						<div className="flex flex-col gap-1.5">
							<Label className="text-xs">{t("field_customer")}</Label>
							<Select
								value={formCustomer}
								onValueChange={setFormCustomer}
							>
								<SelectTrigger className="h-9">
									<SelectValue
										placeholder={t("field_customer")}
									/>
								</SelectTrigger>
								<SelectContent>
									{parties.map((p) => (
										<SelectItem key={p.id} value={p.id}>
											{p.displayName || p.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Source material */}
						<div className="space-y-3">
							{/* Text input */}
							<Textarea
								value={formText}
								onChange={(e) => setFormText(e.target.value)}
								rows={5}
								placeholder={t("create_text_placeholder", {
									defaultValue:
										"Вставьте текст письма, заявки или список позиций клиента...",
								})}
								className={`resize-y transition-colors ${
									dragOver ? "opacity-50" : ""
								}`}
							/>

							{/* File attachment */}
							<input
								ref={fileInputRef}
								type="file"
								accept={ACCEPTED_FILE_TYPES}
								className="hidden"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) setFormFile(file);
								}}
							/>

							{formFile ? (
								<div className="flex items-center gap-3 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20 px-3 py-2">
									<FileUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate">
											{formFile.name}
										</p>
										<p className="text-[11px] text-muted-foreground">
											{(formFile.size / 1024).toFixed(0)} КБ
										</p>
									</div>
									<Button
										size="icon"
										variant="ghost"
										className="h-6 w-6 shrink-0"
										onClick={() => {
											setFormFile(null);
											if (fileInputRef.current)
												fileInputRef.current.value = "";
										}}
									>
										<X className="h-3 w-3" />
									</Button>
								</div>
							) : (
								<button
									type="button"
									className={`flex items-center gap-2 w-full rounded-lg border-2 border-dashed px-3 py-2.5 text-sm text-muted-foreground transition-colors cursor-pointer ${
										dragOver
											? "border-primary bg-primary/5"
											: "border-muted-foreground/20 hover:border-muted-foreground/40"
									}`}
									onClick={() => fileInputRef.current?.click()}
								>
									<FileUp className="h-4 w-4 shrink-0" />
									<span>
										{t("attach_file", {
											defaultValue:
												"Прикрепить файл (PDF, Excel, Word, фото)",
										})}
									</span>
								</button>
							)}
						</div>

						{/* Processing mode selector */}
						<div className="flex flex-col gap-1.5">
							<Label className="text-xs">{t("match_mode")}</Label>
							<div className="grid grid-cols-2 gap-2">
								<Button
									variant="outline"
									type="button"
									className={`flex items-start gap-2 p-2.5 h-auto justify-start ${
										formMatchMode === MatchMode.FAST
											? "border-primary bg-primary/5"
											: "hover:border-muted-foreground/30"
									}`}
									onClick={() => setFormMatchMode(MatchMode.FAST)}
								>
									<Zap
										className={`h-4 w-4 mt-0.5 shrink-0 ${
											formMatchMode === MatchMode.FAST
												? "text-primary"
												: "text-muted-foreground"
										}`}
									/>
									<div className="text-left">
										<div className="text-sm font-medium">
											{t("match_mode_fast")}
										</div>
										<div className="text-xs text-muted-foreground font-normal">
											{t("match_mode_fast_hint")}
										</div>
									</div>
								</Button>
								<Button
									variant="outline"
									type="button"
									className={`flex items-start gap-2 p-2.5 h-auto justify-start ${
										formMatchMode === MatchMode.ACCURATE
											? "border-primary bg-primary/5"
											: "hover:border-muted-foreground/30"
									}`}
									onClick={() => setFormMatchMode(MatchMode.ACCURATE)}
								>
									<Sparkles
										className={`h-4 w-4 mt-0.5 shrink-0 ${
											formMatchMode === MatchMode.ACCURATE
												? "text-primary"
												: "text-muted-foreground"
										}`}
									/>
									<div className="text-left">
										<div className="text-sm font-medium">
											{t("match_mode_accurate")}
										</div>
										<div className="text-xs text-muted-foreground font-normal">
											{t("match_mode_accurate_hint")}
										</div>
									</div>
								</Button>
							</div>
						</div>

						{/* Collapsible details */}
						<div>
							<button
								type="button"
								className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
								onClick={() => setShowDetails(!showDetails)}
							>
								<ChevronRight
									className={`h-3 w-3 transition-transform ${showDetails ? "rotate-90" : ""}`}
								/>
								{t("additional_settings", {
									defaultValue: "Дополнительные параметры",
								})}
							</button>

							{showDetails && (
								<div className="flex flex-col gap-3 mt-3 pt-3 border-t">
									<div className="flex flex-col gap-1.5">
										<Label className="text-xs">
											{t("field_title")}
										</Label>
										<Input
											placeholder={t("auto_from_content", {
												defaultValue: "Авто из содержимого",
											})}
											value={formTitle}
											onChange={(e) => setFormTitle(e.target.value)}
											className="h-8 text-sm"
										/>
									</div>
									<div className="grid grid-cols-2 gap-3">
										<div className="flex flex-col gap-1.5">
											<Label className="text-xs">
												{t("field_currency")}
											</Label>
											<Select
												value={formCurrency}
												onValueChange={setFormCurrency}
											>
												<SelectTrigger className="h-8 text-sm">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="RUB">RUB</SelectItem>
													<SelectItem value="USD">USD</SelectItem>
													<SelectItem value="EUR">EUR</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="flex flex-col gap-1.5">
											<Label className="text-xs">
												{t("field_validity")}
											</Label>
											<Input
												type="number"
												value={formValidity}
												onChange={(e) =>
													setFormValidity(Number(e.target.value))
												}
												min={1}
												className="h-8 text-sm"
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					<DialogFooter>
						<Button
							onClick={() => createMut.mutate()}
							disabled={
								createMut.isPending ||
								(!formTitle.trim() && !formFile && !formText.trim())
							}
						>
							{createMut.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin mr-1" />
							) : null}
							{formText.trim() || formFile
								? t("create_and_process", {
										defaultValue: "Создать и обработать",
									})
								: t("create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<ActionBar />
		</div>
	);
}
